import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Receipt, ArrowLeft, CheckCircle2, Loader2, Building2, Globe } from "lucide-react";
import useInvoicingStore from "../../../../../store/invoicing";
import { Button } from "../../../../../components/ui/Button";
import { Select } from "../../../../../components/ui/Select";
import { Badge } from "../../../../../components/ui/Badge";
import { Card } from "../../../../../components/ui/Card";
import { Field } from "./WizardControls";
import {
  resolveOperatorCountry,
  SUPPORTED_INVOICING_COUNTRIES,
  COUNTRY_DISPLAY_NAME,
  entity,
  envLabel,
} from "./fiscalCopy";
import {
  STEP_META,
  PrerequisitesStep,
  IdentityStep,
  CertificateStep,
  FoliosStep,
  PointOfSaleStep,
  EmissionRulesStep,
  ConnectionTestStep,
  CertificationStep,
  ActivationStep,
} from "./WizardSteps";

// Pasos aplicables según país y ambiente.
function buildSteps(country, environment) {
  const steps = [
    "prerequisites",
    "identity",
    "certificate",
    country === "AR" ? "pos" : "folios",
    "emission_rules",
    "connection_test",
  ];
  if (environment === "production") steps.push("certification");
  steps.push("activation");
  return steps;
}

// Primer paso INCOMPLETO según el config (para retomar donde quedó).
function resumeIndex(steps, config) {
  if (!config) return 0;
  const done = {
    prerequisites: true,
    identity: !!config.taxId,
    certificate: !!config.certificateSubject,
    folios: (config.cafs?.length ?? 0) > 0,
    pos: !!config.pointOfSale,
    emission_rules: !!config.defaultDocType,
    connection_test: !!config.gates?.connectionTested,
    certification: !!config.gates?.certificationApproved,
    activation: !!config.gates?.active,
  };
  const idx = steps.findIndex((s) => !done[s]);
  return idx === -1 ? steps.length - 1 : idx;
}

export default function InvoicingSection({ company }) {
  const config = useInvoicingStore((s) => s.config);
  const loading = useInvoicingStore((s) => s.loading);
  const fetchConfig = useInvoicingStore((s) => s.fetchConfig);
  const resetStore = useInvoicingStore((s) => s.reset);
  const { t } = useTranslation();

  const operatorName = company?.name ?? "";
  // El país NO lo elige el usuario: lo determina el país de operación del operador.
  const country = resolveOperatorCountry(company);
  const supported = SUPPORTED_INVOICING_COUNTRIES.includes(country);

  const [started, setStarted] = useState(false);
  const [environment, setEnvironment] = useState("sandbox");
  const [stepIndex, setStepIndex] = useState(0);
  const [done, setDone] = useState(false);

  // Al desmontar, limpiamos el config en memoria.
  useEffect(() => () => resetStore(), [resetStore]);

  const steps = useMemo(() => buildSteps(country, environment), [country, environment]);

  const begin = async () => {
    const cfg = await fetchConfig(country, environment).catch(() => null);
    const s = buildSteps(country, environment);
    setStepIndex(resumeIndex(s, cfg));
    setDone(!!cfg?.gates?.active);
    setStarted(true);
  };

  const backToStart = () => {
    resetStore();
    setStarted(false);
    setDone(false);
  };

  const goNext = () => setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  const goBack = () => {
    if (stepIndex === 0) backToStart();
    else setStepIndex((i) => Math.max(i - 1, 0));
  };

  const e = entity(country);

  // Mientras carga la empresa, no decidir nada (evita parpadeo de "no disponible").
  if (!company) {
    return (
      <div className="flex items-center justify-center py-10 text-fg-muted" role="status" aria-live="polite">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // ── País no soportado o no determinable ──────────────────────────────────────
  if (!supported) {
    return (
      <div className="max-w-2xl">
        <Card padding="lg" className="flex flex-col items-center text-center gap-3">
          <div className="rounded-full bg-status-warning-soft p-3 text-status-warning">
            <Globe className="w-7 h-7" />
          </div>
          <h3 className="text-lg font-semibold text-fg">
            {t("Facturación electrónica no disponible para tu país")}
          </h3>
          <p className="text-sm text-fg-muted">
            {country
              ? t("Por ahora la emisión está disponible para Chile y Argentina. Tu empresa opera en {{country}}.", {
                  country: COUNTRY_DISPLAY_NAME[country] ?? country,
                })
              : t("No pudimos determinar el país de operación de tu empresa. Defínelo en Organización para habilitar la facturación.")}
          </p>
        </Card>
      </div>
    );
  }

  // ── Pantalla de inicio ───────────────────────────────────────────────────────
  if (!started) {
    return (
      <div className="max-w-2xl">
        <div className="flex items-start gap-3 mb-5">
          <div className="rounded-xl bg-brand-50 p-2.5 text-brand-600">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-fg">{t("Facturación electrónica")}</h3>
            <p className="text-sm text-fg-muted mt-0.5">
              {t("Conecta con el ente recaudador de tu país y emite la factura legal de tus notas de cobro. Te guiamos paso a paso.")}
            </p>
          </div>
        </div>

        {operatorName && (
          <div className="flex items-center gap-2 text-sm text-fg-muted mb-4">
            <Building2 className="w-4 h-4 text-fg-subtle" />
            {operatorName}
          </div>
        )}

        <Card padding="md" className="flex flex-col gap-4">
          {/* País determinado automáticamente por el operador (no editable). */}
          <div>
            <div className="text-sm font-medium text-fg mb-1">{t("País de emisión")}</div>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-bg-muted px-3 py-2.5">
              <Badge tone="brand">{t(e.name)}</Badge>
              <span className="text-sm text-fg">{t(COUNTRY_DISPLAY_NAME[country] ?? country)}</span>
            </div>
            <p className="text-xs text-fg-muted mt-1">
              {t("Determinado por el país de operación de tu empresa.")}
            </p>
          </div>
          <Field
            label={t("Ambiente")}
            help={t("Empieza siempre en pruebas. Producción se habilita tras la prueba de conexión y la certificación.")}
          >
            <Select
              value={environment}
              onChange={(ev) => setEnvironment(ev.target.value)}
              options={[
                { value: "sandbox", label: t("Pruebas — {{name}}", { name: e.sandboxName }) },
                { value: "production", label: t("Producción — {{name}}", { name: e.prodName }) },
              ]}
            />
          </Field>
          <div className="flex justify-end">
            <Button variant="primary" onClick={begin} loading={loading} iconLeft={<Receipt size={16} />}>
              {environment === "production" ? t("Configurar producción") : t("Comenzar configuración")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Pantalla final (configuración activa) ────────────────────────────────────
  if (done) {
    return (
      <div className="max-w-2xl">
        <Card padding="lg" className="flex flex-col items-center text-center gap-3">
          <div className="rounded-full bg-status-success-soft p-3 text-status-success">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h3 className="text-lg font-semibold text-fg">{t("Facturación activada")}</h3>
          <p className="text-sm text-fg-muted">
            {operatorName} {t("emitirá comprobantes ante")} {t(e.authName)} en{" "}
            <strong>{t(envLabel(country, environment))}</strong>.
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge tone={environment === "production" ? "success" : "info"} dot>
              {environment === "production" ? t("Producción") : t("Pruebas")}
            </Badge>
            <Badge tone="brand">{t(e.name)}</Badge>
          </div>
          <Button variant="outline" onClick={backToStart} className="mt-2">
            {t("Volver")}
          </Button>
        </Card>
      </div>
    );
  }

  // ── Shell del wizard ─────────────────────────────────────────────────────────
  const currentKey = steps[stepIndex];
  const stepProps = {
    config,
    country,
    environment,
    operatorName,
    goBack,
    goNext,
    isFirst: false, // "Atrás" en el primer paso vuelve a la pantalla de inicio
  };

  const renderStep = () => {
    switch (currentKey) {
      case "prerequisites":
        return <PrerequisitesStep {...stepProps} />;
      case "identity":
        return <IdentityStep {...stepProps} />;
      case "certificate":
        return <CertificateStep {...stepProps} />;
      case "folios":
        return <FoliosStep {...stepProps} />;
      case "pos":
        return <PointOfSaleStep {...stepProps} />;
      case "emission_rules":
        return <EmissionRulesStep {...stepProps} />;
      case "connection_test":
        return <ConnectionTestStep {...stepProps} />;
      case "certification":
        return <CertificationStep {...stepProps} />;
      case "activation":
        return <ActivationStep {...stepProps} onDone={() => setDone(true)} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl">
      {/* Encabezado: contexto del ambiente, siempre visible (no confundir sandbox/prod). */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <button
          type="button"
          onClick={backToStart}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("Cambiar país/ambiente")}
        </button>
        <div className="flex items-center gap-2">
          <Badge tone="brand">{t(e.name)}</Badge>
          <Badge tone={environment === "production" ? "warning" : "info"} dot>
            {environment === "production"
              ? t("Producción · {{name}}", { name: e.prodName })
              : t("Pruebas · {{name}}", { name: e.sandboxName })}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 items-start">
        {/* Rail de pasos */}
        <ol className="hidden lg:flex lg:flex-col gap-1 bg-bg-muted border border-border rounded-xl p-2">
          {steps.map((key, i) => {
            const state = i < stepIndex ? "done" : i === stepIndex ? "current" : "pending";
            const clickable = i <= stepIndex;
            return (
              <li key={key}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && setStepIndex(i)}
                  className={
                    "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left text-sm transition-colors " +
                    (state === "current"
                      ? "bg-brand-50 text-brand-700 font-medium"
                      : state === "done"
                        ? "text-fg hover:bg-bg-subtle"
                        : "text-fg-subtle cursor-default")
                  }
                >
                  <span
                    className={
                      "flex items-center justify-center w-5 h-5 rounded-full text-[11px] shrink-0 " +
                      (state === "done"
                        ? "bg-status-success text-white"
                        : state === "current"
                          ? "bg-brand-500 text-white"
                          : "bg-bg-subtle text-fg-subtle border border-border")
                    }
                  >
                    {state === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="truncate">{t(STEP_META[key]?.title)}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* Contenido del paso */}
        <div className="min-w-0">
          {/* Progreso compacto (mobile) */}
          <div className="lg:hidden mb-3">
            <div className="flex items-center justify-between text-xs text-fg-muted mb-1">
              <span>
                {t("Paso {{current}} de {{total}}", { current: stepIndex + 1, total: steps.length })}
              </span>
              <span>{t(STEP_META[currentKey]?.title)}</span>
            </div>
            <div className="h-1.5 bg-bg-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 transition-all"
                style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          <Card padding="md">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-fg-muted">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              renderStep()
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
